import { useEffect, useState } from "react";
import { useMcpTool } from "./useMcpTool";

/**
 * Result shape returned by {@link useMcpQuery}. Mirrors the host-app
 * `react-query`-style envelope so consumers can render loading / error /
 * data states declaratively. `refetch` is stable across renders.
 */
export interface UseMcpQueryResult<TRes>
{
    data: TRes | undefined;
    loading: boolean;
    error: Error | undefined;
    /** Re-run the fetch against the current args. Stable across renders. */
    refetch: () => void;
}

/**
 * Options for {@link useMcpQuery}.
 */
export interface UseMcpQueryOptions
{
    /**
     * Gate the fetch on a guard. When false, the hook returns idle state and
     * never invokes — important because hooks must be called unconditionally
     * at the top of the component, so an `enabled` flag is the standard way
     * to express "fetch only when a selection is present" without violating
     * the rules of hooks. Defaults to `true`.
     */
    enabled?: boolean;
}

/**
 * Auto-fetch wrapper over the imperative {@link useMcpTool}. Fires the tool
 * call on mount AND whenever the serialised `args` change. Use for
 * read-shaped MCP tool calls that back a component's render data; for
 * mutations, call {@link useMcpTool} directly and trigger
 * `invoke(...)` from event handlers.
 *
 * Why this lives alongside {@link useMcpTool}: many plugin MCP tools are
 * effectively queries (`list-tasks`, `get-pending-approvals`, …) — paginated
 * lookups that change with filter inputs. The base hook's imperative
 * `invoke` signature is correct for mutations but ergonomically wrong for
 * reads, where every consumer ends up writing the same `useEffect` +
 * AbortController + cancellation-on-unmount boilerplate. This hook absorbs
 * that boilerplate once.
 *
 * @template TArgs The request shape sent to the tool.
 * @template TRes  The response shape the tool returns.
 */
export function useMcpQuery<TArgs, TRes>(
    toolName: string,
    args: TArgs,
    options?: UseMcpQueryOptions,
): UseMcpQueryResult<TRes>
{
    const enabled = options?.enabled ?? true;
    const { invoke } = useMcpTool<TArgs, TRes>(toolName);
    const [data, setData] = useState<TRes | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(enabled);
    const [error, setError] = useState<Error | undefined>(undefined);
    const [tick, setTick] = useState(0);

    // Stringify args for the effect's dependency key so an args object with
    // new identity but unchanged contents does not force a refetch. The
    // shape JSON-serialises trivially because tool args are wire-bound (JSON
    // round-trips on the transport anyway).
    const argsKey = JSON.stringify(args);

    useEffect(() =>
    {
        if (!enabled)
        {
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        invoke(args)
            .then((result) =>
            {
                if (cancelled)
                {
                    return;
                }
                setData(result);
                setError(undefined);
                setLoading(false);
            })
            .catch((err: unknown) =>
            {
                if (cancelled)
                {
                    return;
                }
                setError(err instanceof Error ? err : new Error(String(err)));
                setLoading(false);
            });
        return () =>
        {
            cancelled = true;
        };
        // `args` is intentionally tracked via `argsKey` (serialised contents)
        // so reference-only changes don't refetch. eslint sees this as a
        // missing dep but the effect is correct as-is.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [argsKey, tick, invoke, enabled]);

    return {
        data,
        loading,
        error,
        refetch: () => setTick((t) => t + 1),
    };
}

// ── Response-envelope helpers ────────────────────────────────────
//
// MCP tool responses across the platform's BE conventions arrive in either
// `{ items: T[] }` (raw lists) or `{ rows: T[] }` (paginated envelopes). The
// envelope shape is a BE-side detail plugin authors don't want to encode at
// every read site. {@link ItemsResponse} types the union and
// {@link unwrapItems} normalises to a plain array.

/**
 * Envelope shape that covers both `{ items: T[] }` and `{ rows: T[] }`
 * conventions emitted by platform / plugin MCP read tools. Optional both
 * sides so empty responses still type-check.
 */
export interface ItemsResponse<T>
{
    items?: T[];
    rows?: T[];
}

/**
 * Normalise an {@link ItemsResponse} envelope to a plain array.
 * Returns `[]` when the response is `undefined` (typical pre-first-fetch
 * state) or when neither field is populated.
 */
export function unwrapItems<T>(response: ItemsResponse<T> | undefined): T[]
{
    return response?.items ?? response?.rows ?? [];
}
